//// line chart
import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { reaction } from "mobx";
import { LineChart } from "@mui/x-charts/LineChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import dashboardStore from "../../store/dashboardStore";
import { useTranslation } from "react-i18next";
import styles from "../../styles/dashboard.module.css";
import loadingSvg from "../../../Assets/images/tube-spinner.svg";
import { formatNumber } from "../../utils/tools";

const MAX_Y_AXIS_TICKS = 5;
const MIN_Y_AXIS_TICK = 1;

const getYAxisTicks = (series) => {
  const maxValue = Math.max(
    ...series.flatMap((item) =>
      item.data
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0),
    ),
    MIN_Y_AXIS_TICK,
  );
  const maxExponent = Math.floor(Math.log10(maxValue));
  const majorTicks = Array.from(
    { length: maxExponent + 1 },
    (_, index) => MIN_Y_AXIS_TICK * 10 ** index,
  );

  if (majorTicks.length <= MAX_Y_AXIS_TICKS) {
    return majorTicks;
  }

  const step = Math.ceil(majorTicks.length / MAX_Y_AXIS_TICKS);
  return majorTicks.filter(
    (_, index) => index % step === 0 || index === majorTicks.length - 1,
  );
};

const OGGEbyPeriod = observer(() => {
  const { t } = useTranslation("ActivityMain");

  ////////初始化
  useEffect(() => {
    const dispose = reaction(
      () => [
        dashboardStore.selectedCom,
        dashboardStore.selectedYear,
        dashboardStore.selectedMonth,
        dashboardStore.gwpId,
        dashboardStore.unit,
        dashboardStore.standardId,
      ],
      () => {
        if (
          !dashboardStore.selectedCom ||
          dashboardStore.selectedCom.length === 0
        )
          return;

        if (!dashboardStore.selectedYear) {
          return;
        }

        dashboardStore.getLineChartData();
      },
      {
        fireImmediately: true,
      },
    );

    return () => dispose();
  }, []);

  //對應API的key

  // 當資料變化時處理 xLabels 與 series
  const rawData = dashboardStore.lineChartData;
  const scopeKeys = ["scope1", "scope2", "scope3"];
  const categoryKeys = [
    "category1",
    "category2",
    "category3",
    "category4",
    "category5",
    "category6",
  ];
  const keys = dashboardStore.groupBy === "scope" ? scopeKeys : categoryKeys;

  const unitLabel =
    dashboardStore.unit === "20"
      ? "kgCO2e"
      : dashboardStore.unit === "21"
        ? "tCO2e"
        : "";

  let xLabels = [];
  let chartSeries = [];

  if (rawData) {
    const years = Object.keys(rawData).sort();

    xLabels =
      dashboardStore.format === "monthly"
        ? years.map(
            (year) =>
              `${year}/${String(dashboardStore.selectedMonth).padStart(2, "0")}`,
          )
        : years;

    chartSeries = keys.map((key, index) => ({
      label: key.replace(/(\D+)(\d+)/, (_, word, num) => {
        return t(word.charAt(0).toUpperCase() + word.slice(1)) + " " + num;
      }),
      data: years.map((year) => rawData?.[year]?.[key] ?? 0),
      color: dashboardStore.themeColor[index],
      // MUI 新版防呆：加上 value == null 判斷，避免 tooltips 或軸線計算錯誤
      valueFormatter: (value) =>
        value == null ? "" : `${Number(value).toLocaleString()} ${unitLabel}`,
    }));
  }

  // 判斷 Value 是不是都是 0
  const isAllZero =
    chartSeries.length > 0 &&
    chartSeries.every((item) => item.data.every((value) => value === 0));
  const yAxisTicks = getYAxisTicks(chartSeries);

  return (
    <div className={`${styles.chartContent} ${styles.grid_8}`}>
      <div className={styles.chartTitle}>
        {t("Organizational Greenhouse Gas Emission by Period")}
      </div>
      {/* <div className={styles.chartBody}> */}
      <div className={styles.chart}>
        {/**loading page */}
        {dashboardStore.lineChartDataLoading ? (
          <div className={styles.loading}>
            <img
              className={styles.loadingImg}
              src={loadingSvg}
              alt="loadingSvg"
            />
          </div>
        ) : isAllZero ? (
          <div className={styles.noData}>{t("No data")}!</div>
        ) : (
          <LineChart
            //   width={700}
            height={250}
            series={chartSeries}
            xAxis={[{ scaleType: "point", data: xLabels }]}
            yAxis={[
              {
                label: `${t("Total Emissions")} (${unitLabel})`,
                // MUI 新版防呆
                width: 120,
                scaleType: "symlog",
                tickNumber: yAxisTicks.length,
                tickInterval: yAxisTicks,
                tickLabelStyle: {
                  fontSize: 12,
                },
                labelStyle: {
                  fontSize: 12,
                },
                valueFormatter: (value) => formatNumber(value),
              },
            ]}
            margin={{ left: 45, right: 20, top: 15, bottom: 5 }}
            slotProps={{
              legend: { hidden: true },
              // tooltip: { trigger: 'axis' }
            }}
            sx={{
              // 使用 axisClasses 來確保升級後的 CSS 類別依然能正確對應
              [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: "translate(-16px, 0)",
              },
            }}
          />
        )}
      </div>
    </div>
  );
});

export default OGGEbyPeriod;
